import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import {
  listMicInputs,
  type ActiveMic,
  type MicChoice,
  type MicInputs,
  type MicSwitchReason,
  type RecordingInputEvent,
} from '@clarix/meeting-recording';
import { toast } from '@clarix/ui';
import type { AppSelectOption } from '@clarix/ui-shared';
import { savedMic } from './browser';
import { QM_MIC } from './constants';
import { QM_COPY } from './copy';
import { activeValue, choiceFor, defaultDevice, defaultKey, micName, micOptions, savedValue } from './micOptions';
import { useMicDevices, type MicDevices } from './useMicDevices';

/** The running engine's mic controls; every call is a no-op / null when nothing is recording. */
export interface EngineMic {
  getActiveMic: () => ActiveMic | null;
  getAnalyser: () => AnalyserNode | null;
  switchMic: (choice: MicChoice | null, reason: MicSwitchReason) => Promise<ActiveMic>;
}

export interface QuickMeetingMicValue {
  options: AppSelectOption[];
  /** Before recording the saved choice; during one the device actually capturing ('' = not listed). */
  value: string;
  /** Shown when `value` matches no option: the live track's own label. */
  placeholder: string;
  choose: (value: string) => void;
  /** The mic went away and no other could be opened: the take continues as silence. */
  inputLost: boolean;
  onInputEvent: (event: RecordingInputEvent) => void;
}

/** One notice and one prompt at a time: a newer one replaces the older. */
const TOAST_ID = { NOTICE: 'qm-mic', PROMPT: 'qm-mic-prompt' } as const;

/** Fresh labels for a notice: the picker's list may not have settled after a device change. */
const readInputs = (): Promise<MicInputs> => listMicInputs();

interface Params {
  userId: string | null;
  /** Recording or paused: the engine holds a live mic. */
  capturing: boolean;
  engine: EngineMic;
}

/**
 * The Quick Meeting mic: the saved choice, both pickers, the engine's input events as toasts, and
 * following the OS default mid-take. Pinned (a chosen device, or any pick this take) never moves
 * on its own; it only offers.
 */
export function useQuickMeetingMic({ userId, capturing, engine }: Params): QuickMeetingMicValue {
  const inputs = useMicDevices(capturing);
  const raw = useSyncExternalStore(savedMic.subscribe, () => savedMic.raw(userId), () => null);
  const saved = useMemo(() => savedMic.parse(raw), [raw]);
  const [active, setActive] = useState<ActiveMic | null>(null);
  const [lost, setLost] = useState(false);

  const value = capturing ? activeValue(inputs, active) : savedValue(inputs, saved);
  const pinnedRef = useRef(false);
  /** The mic before the latest event: what Undo goes back to. */
  const lastMicRef = useRef<ActiveMic | null>(null);
  const baselineRef = useRef<MicDevices | null>(null);
  const latest = useRef({ userId, capturing, engine, inputs, saved, value, lost });
  useEffect(() => {
    latest.current = { userId, capturing, engine, inputs, saved, value, lost };
  });

  /** Resolves true once the mic changed; a failure arrives as `switch-failed`, with its own toast. */
  const switchTo = useCallback(
    (choice: MicChoice | null, reason: MicSwitchReason): Promise<boolean> =>
      latest.current.engine.switchMic(choice, reason).then(
        () => {
          // Only a switch that happened changes pinning: device pins, "System default" follows the OS, Undo pins.
          if (reason === 'undo') pinnedRef.current = true;
          else if (reason === 'user') pinnedRef.current = choice != null;
          return true;
        },
        () => false,
      ),
    [],
  );

  const offer = useCallback(
    (title: string, choice: MicChoice) => {
      toast.info({
        id: TOAST_ID.PROMPT,
        title,
        description: QM_COPY.micSwitchQuestion,
        duration: QM_MIC.PROMPT_MS,
        action: { label: QM_COPY.micSwitch, onClick: () => void switchTo(choice, 'user') },
      });
    },
    [switchTo],
  );

  const announceSwitch = useCallback(
    async (mic: ActiveMic, reason: MicSwitchReason, before: ActiveMic | null) => {
      if (reason === 'user' || reason === 'undo') return;
      const names = await readInputs();
      const name = micName(names, mic);
      if (reason === 'system-default') {
        const back = before ? choiceFor(names, before) : null;
        toast.info({
          id: TOAST_ID.NOTICE,
          title: QM_COPY.micSwitchedTo(name),
          duration: QM_MIC.UNDO_MS,
          ...(back ? { action: { label: QM_COPY.undo, onClick: () => void switchTo(back, 'undo') } } : {}),
        });
      } else {
        const title = reason === 'disconnected' ? QM_COPY.micDisconnected(name) : QM_COPY.micSwitchFallback(name);
        toast.warning({ id: TOAST_ID.NOTICE, title, duration: QM_MIC.NOTICE_MS });
      }
    },
    [switchTo],
  );

  const announceFailure = useCallback(
    async (event: Extract<RecordingInputEvent, { type: 'switch-failed' }>, before: ActiveMic | null) => {
      // An unplug with nothing to fall back to shows the persistent in-pill warning instead.
      if (!event.mic && event.reason === 'disconnected') return;
      const names = await readInputs();
      const target = event.target?.label ?? (event.target ? QM_COPY.unnamedMic : QM_COPY.systemDefault);
      const title = event.error.code === 'RATE_MISMATCH' ? QM_COPY.micCantSwitchHere(target) : event.error.message;
      const moved = event.mic && before && event.mic.groupId !== before.groupId;
      toast.error({
        id: TOAST_ID.NOTICE,
        title,
        ...(moved && event.mic ? { description: QM_COPY.micSwitchFallback(micName(names, event.mic)) } : {}),
        duration: QM_MIC.NOTICE_MS,
      });
    },
    [],
  );

  const announceFallback = useCallback(async (mic: ActiveMic, from: MicChoice) => {
    const names = await readInputs();
    toast.warning({
      id: TOAST_ID.NOTICE,
      title: QM_COPY.micFellBack(from.label ?? QM_COPY.savedMic, micName(names, mic)),
      duration: QM_MIC.NOTICE_MS,
    });
  }, []);

  const onInputEvent = useCallback(
    (event: RecordingInputEvent) => {
      setActive(latest.current.engine.getActiveMic());
      const before = lastMicRef.current;
      switch (event.type) {
        case 'opened':
          lastMicRef.current = event.mic;
          // Pinned only when the take really started on the chosen device; a missing one follows the OS.
          pinnedRef.current = latest.current.saved != null && !event.fellBackFrom;
          setLost(false);
          if (event.fellBackFrom) void announceFallback(event.mic, event.fellBackFrom);
          return;
        case 'switched':
          lastMicRef.current = event.mic;
          setLost(false);
          void announceSwitch(event.mic, event.swap.reason, before);
          return;
        case 'switch-failed':
          lastMicRef.current = event.mic;
          void announceFailure(event, before);
          return;
        case 'input-lost':
          lastMicRef.current = null;
          setLost(true);
          return;
      }
    },
    [announceFailure, announceFallback, announceSwitch],
  );

  const reactToDevices = useCallback(
    (before: MicDevices, now: MicDevices) => {
      // Lost, the take records silence: a mic coming back is taken (following the OS), or offered when pinned.
      if (latest.current.lost) {
        // A replug inside the list debounce re-lists the same id, so any listed mic counts, not only a new one.
        const back =
          now.devices.find((d) => !before.devices.some((b) => b.deviceId === d.deviceId)) ??
          defaultDevice(now) ??
          now.devices[0];
        if (!back) return;
        if (!pinnedRef.current) void switchTo(null, 'system-default');
        else offer(QM_COPY.micNewDetected(back.label || QM_COPY.unnamedMic), { deviceId: back.deviceId, label: back.label });
        return;
      }
      const current = latest.current.engine.getActiveMic();
      if (!current) return;
      const target = defaultDevice(now);
      const key = defaultKey(now);
      const onDefault = now.systemDefault?.groupId === current.groupId || target?.deviceId === current.deviceId;
      const defaultMoved = key != null && key !== defaultKey(before) && !onDefault;
      if (defaultMoved) {
        if (!pinnedRef.current) void switchTo(null, 'system-default');
        else if (target) {
          offer(QM_COPY.micDefaultChanged(target.label || QM_COPY.unnamedMic), { deviceId: target.deviceId, label: target.label });
        }
      }
      // Virtual inputs (Teams, Zoom, BlackHole) appear like headsets: only ever offered, never taken.
      const added = now.devices.find(
        (d) =>
          !before.devices.some((b) => b.deviceId === d.deviceId) &&
          d.groupId !== now.systemDefault?.groupId &&
          d.groupId !== current.groupId,
      );
      if (added) offer(QM_COPY.micNewDetected(added.label || QM_COPY.unnamedMic), { deviceId: added.deviceId, label: added.label });
    },
    [offer, switchTo],
  );

  // The first list of a take is its baseline; each later one is compared with the one before.
  useEffect(() => {
    if (!capturing) {
      baselineRef.current = null;
      return;
    }
    const before = baselineRef.current;
    baselineRef.current = inputs;
    // An empty first list is usually pre-permission; only a lost mic reacts to it (the mic came back).
    if (before?.listed && inputs.listed && (before.devices.length > 0 || latest.current.lost)) {
      reactToDevices(before, inputs);
    }
  }, [capturing, inputs, reactToDevices]);

  const choose = useCallback(
    (next: string) => {
      const cur = latest.current;
      if (next === cur.value) return;
      const label = cur.inputs.devices.find((d) => d.deviceId === next)?.label ?? (next === cur.saved?.deviceId ? cur.saved.label : undefined);
      const choice: MicChoice | null = next === QM_MIC.SYSTEM_DEFAULT ? null : { deviceId: next, ...(label ? { label } : {}) };
      if (!cur.capturing) {
        savedMic.set(cur.userId, choice);
        return;
      }
      // Remember a mid-take pick only if it opened, so the next take doesn't start on a fallback.
      void switchTo(choice, 'user').then((switched) => {
        if (switched) savedMic.set(cur.userId, choice);
      });
    },
    [switchTo],
  );

  return {
    options: micOptions(inputs, saved),
    value,
    placeholder: !capturing ? QM_COPY.systemDefault : active?.label || (lost ? QM_COPY.micInputLostShort : QM_COPY.systemDefault),
    choose,
    inputLost: capturing && lost,
    onInputEvent,
  };
}
